import React, { useState } from "react";
import { Box, Checkbox, Collapse, IconButton, Typography, makeStyles } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import MenuIcon from "@material-ui/icons/Menu";
import { useTranslation } from "react-i18next";
import { CustomMultiSelect } from "components/Shared/mui-formik-inputs";
import ZipcodeTourPin from "./ZipcodeTourPin";
import { isSidebarZipHighlighted } from "util/zipcodeVisualization";

const useStyles = makeStyles({
  root: {
    color: "#fff",
    padding: "12px 16px",
  },
  tourRow: {
    display: "flex",
    alignItems: "center",
    padding: "8px 4px",
    cursor: "pointer",
    borderBottom: "1px solid #333",
  },
  tourName: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
  },
  zipRow: {
    padding: "6px 8px 6px 48px",
    fontSize: 13,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#2a2a2a",
    },
  },
  zipRowHighlighted: {
    backgroundColor: "#2a2a2a",
    outline: "1px solid rgba(255, 255, 255, 0.35)",
    outlineOffset: -1,
  },
  zipWithCoords: {
    color: "#fff",
  },
  zipWithoutCoords: {
    color: "#757575",
  },
  toolbarRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
    minWidth: 0,
  },
  viewAll: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  supplierFilter: {
    flex: 1,
    minWidth: 0,
    "& .MuiFormControl-root": {
      marginTop: "0 !important",
    },
    "& .MuiInputBase-root": {
      color: "#fff",
      fontSize: 13,
    },
    "& .MuiSelect-select": {
      color: "#fff",
    },
    "& .MuiSelect-icon": {
      color: "#fff !important",
    },
  },
});

export default function ZipcodesVisualizationSidebar({
  tourGroups,
  selectedTourIds,
  onTourSelectionChange,
  seeAllSuppliers = false,
  supplierOptions = [],
  selectedSupplierIds = [],
  suppliersFilterReady = false,
  onSupplierFilterChange,
  departureOptions = [],
  selectedDepartureTimes = [],
  onDepartureFilterChange,
  onZipcodeClick,
  onZipcodeHover,
  hoveredId = null,
  mapPoints = [],
  onToggleSidebar,
}) {
  const classes = useStyles();
  const { t } = useTranslation("common");
  const [expanded, setExpanded] = useState({});
  const [expandedDepartures, setExpandedDepartures] = useState({});

  const selectedSet = new Set(selectedTourIds);
  const allTourIds = tourGroups.map((g) => g.tourId);
  const allSelected = allTourIds.length > 0 && allTourIds.every((id) => selectedSet.has(id));
  const someSelected = allTourIds.some((id) => selectedSet.has(id));

  const toggleViewAll = () => {
    if (allSelected) {
      onTourSelectionChange([]);
    } else {
      onTourSelectionChange(allTourIds);
    }
  };

  const toggleTour = (tourId) => {
    if (selectedSet.has(tourId)) {
      onTourSelectionChange(selectedTourIds.filter((id) => id !== tourId));
    } else {
      onTourSelectionChange([...selectedTourIds, tourId]);
    }
  };

  const toggleExpanded = (tourId) => {
    setExpanded((prev) => ({ ...prev, [tourId]: !prev[tourId] }));
  };

  const toggleDepExpanded = (tourId, depId) => {
    const key = `${tourId}-${depId}`;
    setExpandedDepartures((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.toolbarRow} style={{ marginBottom: 12 }}>
        {onToggleSidebar && (
          <IconButton
            onClick={onToggleSidebar}
            aria-label={t("Toggle sidebar")}
            style={{ color: "#fff", marginLeft: -15, flexShrink: 0 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        {seeAllSuppliers && suppliersFilterReady && (
          <Box className={classes.supplierFilter}>
            <CustomMultiSelect
              values={supplierOptions}
              errors={{}}
              setValues={selectedSupplierIds}
              onChange={(e) => onSupplierFilterChange?.(e.target.value)}
              label={t("Supplier")}
              labelId="zipcode-visualization-suppliers"
              backgroundColor="#1e1e1e"
              borderColor="#525252"
              labelColor="white"
              name="supplier_filter"
              disabled={!supplierOptions.length}
            />
          </Box>
        )}
        <Box className={classes.supplierFilter}>
          <CustomMultiSelect
            values={departureOptions}
            errors={{}}
            setValues={selectedDepartureTimes}
            onChange={(e) => onDepartureFilterChange?.(e.target.value)}
            label={t("Departure")}
            labelId="zipcode-visualization-departures"
            backgroundColor="#1e1e1e"
            borderColor="#525252"
            labelColor="white"
            name="departure_filter"
            disabled={!departureOptions.length}
          />
        </Box>
      </Box>

      <Box className={classes.viewAll} style={{ marginBottom: 16 }}>
        <Checkbox
          checked={allSelected}
          indeterminate={!allSelected && someSelected}
          onChange={toggleViewAll}
          color="primary"
        />
        <Typography variant="body1" style={{ whiteSpace: "nowrap" }}>
          {t("View All")}
        </Typography>
      </Box>

      {tourGroups.map((tour) => {
        const isExpanded = Boolean(expanded[tour.tourId]);
        const checked = selectedSet.has(tour.tourId);
        return (
          <Box key={tour.tourId}>
            <Box className={classes.tourRow}>
              <Checkbox
                checked={checked}
                onChange={() => toggleTour(tour.tourId)}
                color="primary"
                onClick={(e) => e.stopPropagation()}
              />
              <ZipcodeTourPin color={tour.color} size={22} />
              <Typography className={classes.tourName}>
                {tour.tourName}
                <Box component="span" ml={1} style={{ color: "#ADADAD", fontSize: 12 }}>
                  ({tour.zipcodes.filter((z) => z.hasCoordinates).length}/{tour.zipcodes.length})
                </Box>
              </Typography>
              <IconButton
                size="small"
                style={{ color: "#fff" }}
                onClick={() => toggleExpanded(tour.tourId)}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={isExpanded}>
              {(tour.departures || []).map((dep) => {
                const depKey = `${tour.tourId}-${dep.id}`;
                const isDepExpanded = Boolean(expandedDepartures[depKey]);
                return (
                  <Box key={dep.id} style={{ paddingLeft: 16 }}>
                    <Box
                      className={classes.tourRow}
                      onClick={() => toggleDepExpanded(tour.tourId, dep.id)}
                      style={{ borderBottom: "none", padding: "6px 4px" }}
                    >
                      <IconButton size="small" style={{ color: "#ADADAD", padding: 0 }}>
                        {isDepExpanded ? <ExpandLessIcon style={{ fontSize: 20 }} /> : <ExpandMoreIcon style={{ fontSize: 20 }} />}
                      </IconButton>
                      <Typography style={{ flex: 1, fontSize: 13, marginLeft: 8 }}>
                        {t("Departure")}: {t(dep.delivery_timing)}
                        <Box component="span" ml={1} style={{ color: "#ADADAD", fontSize: 11 }}>
                          ({dep.zipcodes.filter((z) => z.hasCoordinates).length}/{dep.zipcodes.length})
                        </Box>
                      </Typography>
                    </Box>
                    <Collapse in={isDepExpanded}>
                      {dep.zipcodes.map((zip) => {
                        const highlighted = isSidebarZipHighlighted(zip, hoveredId, mapPoints);
                        return (
                          <Box
                            key={zip.id}
                            className={`${classes.zipRow} ${zip.hasCoordinates ? classes.zipWithCoords : classes.zipWithoutCoords
                              } ${highlighted ? classes.zipRowHighlighted : ""}`}
                            onClick={() => onZipcodeClick(zip)}
                            onMouseEnter={() => onZipcodeHover?.(zip.hasCoordinates ? zip.id : null)}
                            onMouseLeave={() => onZipcodeHover?.(null)}
                            style={{ paddingLeft: 36 }}
                          >
                            {zip.zipcode}
                            <Box component="span" ml={1} style={{ color: "#888", fontSize: 11 }}>
                              ({t(dep.delivery_timing)})
                            </Box>
                            {!zip.hasCoordinates ? ` (${t("No geolocation")})` : ""}
                          </Box>
                        );
                      })}
                    </Collapse>
                  </Box>
                );
              })}
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}
